import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Lock } from "lucide-react";
import AuthShell from "@/components/auth/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { TurnstileGate, turnstileError } from "@/components/common/TurnstileGate";
import { useTurnstile } from "@/hooks/usePublicConfig";
import { isProdHost } from "@/config";

export default function Login() {
  const navigate = useNavigate();
  const { login, verifyTwoFactor } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const { enabled: tsEnabled, siteKey: tsSiteKey } = useTurnstile();
  const tsRequired = tsEnabled && !!tsSiteKey;
  const [tsToken, setTsToken] = useState(null);
  const tsRef = useRef(null);

  // 2FA login challenge state (held in memory only — never persisted).
  const [twofa, setTwofa] = useState(null); // { ticket, phone_hint }
  const [code, setCode] = useState("");

  const resetTurnstile = () => {
    setTsToken(null);
    tsRef.current?.reset();
  };

  const backToPassword = (msg) => {
    setTwofa(null);
    setCode("");
    resetTurnstile();
    if (msg) toast.error(msg);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (tsRequired && !tsToken) return toast.error("Please complete the verification.");
    setLoading(true);
    try {
      const data = await login(email.trim(), password, tsToken || undefined);
      if (data?.twofa_required) {
        // Move to the SMS code step. No token yet.
        setTwofa({ ticket: data.twofa_ticket, phone_hint: data.phone_hint });
        resetTurnstile();
        toast.success(`We texted a code to ${data.phone_hint || "your phone"}.`);
        return;
      }
      toast.success("Welcome back");
      navigate("/app/chats");
    } catch (err) {
      resetTurnstile();
      const { isTurnstile, message } = turnstileError(err);
      toast.error(isTurnstile ? message || "Security check failed — please try again." : message || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    const c = code.trim();
    if (c.length < 4) return toast.error("Enter the 6-digit code from the text.");
    setLoading(true);
    try {
      await verifyTwoFactor(twofa.ticket, c);
      toast.success("Welcome back");
      navigate("/app/chats");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 410) {
        backToPassword("That code expired — please sign in again for a fresh code.");
      } else {
        const d = err?.response?.data?.detail;
        toast.error((typeof d === "string" && d) || "That code didn't match — try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setEmail("demo@bloqtalk.app");
    setPassword("BloqTalk2026!");
  };

  return (
    <AuthShell
      title={twofa ? "Two-factor verification" : "Sign in"}
      subtitle={twofa ? `Enter the 6-digit code we texted to ${twofa.phone_hint || "your phone"}.` : "Welcome back to your trust-gated inbox."}
      footer={
        twofa ? (
          <button type="button" onClick={() => backToPassword()} className="font-semibold text-bloq-mint hover:underline" data-testid="twofa-back-link">
            Back to sign in
          </button>
        ) : (
          <>
            New to BLOQ Talk?{" "}
            <Link to="/signup" className="font-semibold text-bloq-mint hover:underline" data-testid="go-signup-link">
              Create an account
            </Link>
          </>
        )
      }
    >
      {twofa ? (
        <form onSubmit={submitCode} className="space-y-5" data-testid="login-2fa-form">
          <div className="space-y-2">
            <Label htmlFor="twofa-code" className="text-xs uppercase tracking-wider text-bloq-muted">
              Verification code
            </Label>
            <Input
              id="twofa-code"
              inputMode="numeric"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
              placeholder="123456"
              className="h-12 rounded-xl border-white/12 bg-bloq-bg px-4 text-center text-lg tracking-[0.4em] text-white placeholder:text-bloq-muted focus-visible:ring-bloq-mint"
              data-testid="login-2fa-code-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="h-13 w-full rounded-full bloq-gradient py-3.5 text-base font-bold text-bloq-bg shadow-mint hover:opacity-90 disabled:opacity-60"
            data-testid="login-2fa-submit-btn"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Verify & sign in"}
          </Button>
          <p className="text-center text-[11px] leading-relaxed text-bloq-muted" data-testid="login-2fa-note">
            Didn't get it? Go back and sign in again for a fresh code. BLOQ will never ask for your code by phone or email.
          </p>
        </form>
      ) : (
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-xs uppercase tracking-wider text-bloq-muted">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@bloqtalk.app"
            className="h-12 rounded-xl border-white/12 bg-bloq-bg px-4 text-white placeholder:text-bloq-muted focus-visible:ring-bloq-mint"
            data-testid="login-email-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-xs uppercase tracking-wider text-bloq-muted">
            Password
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={show ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
              className="h-12 rounded-xl border-white/12 bg-bloq-bg px-4 pr-11 text-white placeholder:text-bloq-muted focus-visible:ring-bloq-mint"
              data-testid="login-password-input"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-bloq-muted hover:text-white"
              data-testid="toggle-password-visibility"
              tabIndex={-1}
            >
              {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          <div className="flex justify-end">
            <Link to="/forgot-password" className="text-xs font-semibold text-bloq-muted transition-colors hover:text-bloq-mint" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>
        </div>

        {tsRequired && (
          <TurnstileGate
            ref={tsRef}
            action="login"
            siteKey={tsSiteKey}
            onToken={setTsToken}
            className="flex justify-center"
          />
        )}

        <Button
          type="submit"
          disabled={loading || (tsRequired && !tsToken)}
          className="h-13 w-full rounded-full bloq-gradient py-3.5 text-base font-bold text-bloq-bg shadow-mint hover:opacity-90 disabled:opacity-60"
          data-testid="login-submit-btn"
        >
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
        </Button>

        <p className="text-center text-[11px] leading-relaxed text-bloq-muted" data-testid="login-antiphishing-note">
          BLOQ will never ask for your password or verification codes.
        </p>

        {!isProdHost() && (
          <button
            type="button"
            onClick={fillDemo}
            className="flex w-full items-center justify-center gap-2 rounded-full border border-white/10 bg-bloq-s2 py-2.5 text-sm text-bloq-muted transition-colors hover:text-white"
            data-testid="use-demo-btn"
          >
            <Lock className="h-4 w-4" /> Use demo account
          </button>
        )}
      </form>
      )}
    </AuthShell>
  );
}
